import React, { useState, useEffect } from 'react';
import { radiologyTestService, RadiologyTest } from '../../lib/services.radiology';
import { Search } from 'lucide-react';
import toast from 'react-hot-toast';

export default function RadiologyTestCatalog() {
  const [tests, setTests] = useState<RadiologyTest[]>([]);
  const [filteredTests, setFilteredTests] = useState<RadiologyTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterModality, setFilterModality] = useState<string>('all');

  useEffect(() => {
    loadTests();
  }, []);

  useEffect(() => {
    filterTests();
  }, [tests, search, filterModality]);

  const loadTests = async () => {
    try {
      const data = await radiologyTestService.getAll();
      setTests(data);
    } catch (error) {
      console.error('Failed to load tests:', error);
      toast.error('Failed to load test catalog');
    } finally {
      setLoading(false);
    }
  };

  const filterTests = () => {
    let filtered = tests;

    if (filterModality !== 'all') {
      filtered = filtered.filter(t => t.modality === filterModality);
    }

    if (search) {
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.code.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredTests(filtered);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Radiology Test Catalog</h1>
        <p className="text-gray-600">Browse available imaging tests</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search tests..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <select
            value={filterModality}
            onChange={(e) => setFilterModality(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="all">All Modalities</option>
            <option value="XR">X-Ray</option>
            <option value="USG">Ultrasound</option>
            <option value="CT">CT Scan</option>
            <option value="MRI">MRI</option>
            <option value="MAMMO">Mammography</option>
            <option value="DXA">DEXA</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTests.map((test) => (
            <div key={test.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-medium text-gray-900">{test.name}</h3>
                  <p className="text-sm text-gray-600">{test.code}</p>
                </div>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                  {test.modality}
                </span>
              </div>
              {test.body_part && (
                <p className="text-sm text-gray-600 mb-2">Body Part: {test.body_part}</p>
              )}
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                <span className="text-sm font-semibold text-gray-900">₹{test.price}</span>
                <span className="text-sm text-gray-600">{test.tat_hours}h TAT</span>
              </div>
            </div>
          ))}
        </div>

        {filteredTests.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No tests found
          </div>
        )}
      </div>
    </div>
  );
}
